import { ExportMenu } from "@/components/ExportMenu";
import Pagination from "@/components/Pagination";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDate, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import Modal from "@/components/Modal";
import { PackagePlus, ClipboardCheck, Search } from "lucide-react";
import { PackEquiv } from "@/lib/units";

const MOVE_LABELS = { reception: "Réception", adjustment: "Ajustement", loss: "Casse / Perte", transfer_out: "Transfert sortie", transfer_in: "Transfert entrée", return_in: "Retour client", sale: "Vente", inventory_adjust: "Inventaire", void_return: "Annulation vente" };

export default function Stock() {
  const { current } = useActivity();
  const { user } = useAuth();
  const canWriteStock = ["owner", "admin", "manager", "storekeeper"].includes(user?.role) || !!user?.can_adjust_stock;
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [locs, setLocs] = useState([]);
  const [sLoc, setSLoc] = useState("");
  const [tab, setTab] = useState("stock");
  const [moveOpen, setMoveOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", type: "reception", quantity: "", unit: "base", unit_cost: "", reason: "" });
  const [counts, setCounts] = useState({});
  const [invReason, setInvReason] = useState("");
  const [invComment, setInvComment] = useState("");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sp] = useSearchParams();
  const [alertOnly, setAlertOnly] = useState(sp.get("filter") === "alert");
  const [consolidating, setConsolidating] = useState(false);

  const load = async () => {
    if (!current) return;
    const [s, m, l] = await Promise.all([
      api.get(`/stock?activity_id=${current.id}`),
      api.get(`/movements?activity_id=${current.id}`),
      api.get(`/locations?activity_id=${current.id}`),
    ]);
    setProducts(s.data); setMovements(m.data);
    // Mode simple : le stock vit dans le point de vente unique de l'activité (pas l'entrepôt).
    const visibleLocs = current?.stock_mode === "simple" ? l.data.filter((x) => x.type !== "warehouse") : l.data;
    setLocs(visibleLocs);
    setSLoc((p) => {
      // Un seul emplacement : toujours le sélectionner (même via lien d'alerte) pour permettre les mouvements/inventaires.
      if (visibleLocs.length === 1) return visibleLocs[0].id;
      if (alertOnly) return "ALL";
      return (p && visibleLocs.some((x) => x.id === p)) ? p : (visibleLocs[0]?.id || "");
    });
  };
  useEffect(() => { load(); }, [current]);

  const submitMove = async () => {
    const effLoc = (sLoc === "ALL" && locs.length === 1) ? locs[0].id : sLoc;
    if (effLoc === "ALL") return toast.error("Choisissez un emplacement précis pour enregistrer un mouvement.");
    const baseQty = Number(form.quantity || 0) * unitFactor;
    try {
      await api.post("/movements", { activity_id: current.id, product_id: form.product_id, type: form.type, quantity: baseQty, location_id: effLoc || null, unit_cost: form.unit_cost ? Number(form.unit_cost) : null, reason: form.reason });
      toast.success("Mouvement enregistré"); setMoveOpen(false); setForm({ product_id: "", type: "reception", quantity: "", unit: "base", unit_cost: "", reason: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const quickEntry = (p) => { setForm({ product_id: p.id, type: "reception", quantity: "", unit: "base", unit_cost: "", reason: "Réapprovisionnement" }); setMoveOpen(true); };

  const submitInv = async () => {
    const effLoc = (sLoc === "ALL" && locs.length === 1) ? locs[0].id : sLoc;
    if (effLoc === "ALL") return toast.error("Choisissez un emplacement précis pour l'inventaire.");
    const list = products.map((p) => ({ product_id: p.id, counted: Number(counts[p.id] ?? locStock(p)) }));
    try {
      const { data } = await api.post("/inventory", { activity_id: current.id, location_id: effLoc || null, counts: list, reason: invReason || null, comment: invComment || null });
      const diffs = data.results.filter((r) => r.diff !== 0).length;
      toast.success(`Inventaire clôturé · ${diffs} écart(s) ajusté(s)`); setInvOpen(false); setCounts({}); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const consolidate = async () => {
    setConsolidating(true);
    try {
      const { data } = await api.post("/stock/consolidate", { activity_id: current.id });
      toast.success(`Stock rattaché à « ${data.location} » (${data.moved} mouvement(s))`); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setConsolidating(false); }
  };

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  const locStock = (p) => (sLoc && sLoc !== "ALL" ? (p.stock_by_location?.[sLoc] ?? 0) : p.stock);
  const stockStatus = (p) => {
    if (p.track_stock === false) return null;
    const q = Number(locStock(p)) || 0;
    if (q <= 0) return "out";
    if (q <= (Number(p.reorder_level) || 0)) return "low";
    return "ok";
  };
  const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const searched = q.trim() ? products.filter((p) => { const t = norm(q); return norm(p.name).includes(t) || norm(p.base_unit).includes(t) || norm(p.barcode).includes(t) || (p.packagings || []).some((pk) => norm(pk.barcode).includes(t)); }) : products;
  const viewProducts = alertOnly ? searched.filter((p) => ["out", "low"].includes(stockStatus(p))) : searched;
  const sLocName = sLoc === "ALL" ? "Stock global" : (locs.find((l) => l.id === sLoc)?.name || "Emplacement");
  const posLocs = locs.filter((l) => l.type === "pos");
  const singleShop = current?.stock_mode !== "advanced" && posLocs.length <= 1;
  const posId = posLocs[0]?.id;
  const stranded = singleShop && posId && products.some((p) => (Number(p.stock) || 0) > (Number(p.stock_by_location?.[posId]) || 0) + 0.001);
  const visMoves = (sLoc && sLoc !== "ALL") ? movements.filter((m) => m.location_id === sLoc) : movements;

  const selProd = products.find((p) => p.id === form.product_id);
  const unitOpts = selProd
    ? [{ key: "base", label: selProd.base_unit, factor: 1 }, ...(selProd.packagings || []).map((pk) => ({ key: pk.name, label: `${pk.name} (× ${pk.factor} ${selProd.base_unit})`, factor: pk.factor }))]
    : [{ key: "base", label: "unité", factor: 1 }];
  const unitFactor = unitOpts.find((o) => o.key === form.unit)?.factor || 1;
  const baseQtyPreview = Number(form.quantity || 0) * unitFactor;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Stock</h1>
          <p className="text-sm text-[#64748B] mt-1">Activité : {current.name}</p></div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {current?.stock_mode !== "simple" && (
          <select className="inp !w-auto flex-1 sm:flex-none" value={sLoc} onChange={(e) => { setSLoc(e.target.value); setPage(1); }} data-testid="stock-location-select">
            <option value="ALL">Tous les emplacements (global)</option>
            {locs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type === "warehouse" ? " (entrepôt)" : ""}</option>)}
          </select>
          )}
          <button onClick={() => setInvOpen(true)} className={`btn-outline ${canWriteStock ? "" : "hidden"}`} data-testid="open-inventory-btn"><ClipboardCheck className="w-4 h-4" /> Inventaire</button>
          <ExportMenu dataset="stock" params={{ activity_id: current.id, location_id: sLoc !== "ALL" ? sLoc : "" }} />
          <button onClick={() => setMoveOpen(true)} className={`btn-primary ${canWriteStock ? "" : "hidden"}`} data-testid="open-movement-btn"><PackagePlus className="w-4 h-4" /> Entrée / Sortie</button>
        </div>
      </div>

      {stranded && (
        <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="stock-consolidate-banner">
          <div className="flex-1">
            <div className="font-semibold text-[#92400E]">Du stock n'est pas encore rattaché à votre boutique</div>
            <div className="text-sm text-[#B45309] mt-0.5">Une partie du stock a été saisie avant d'être dirigée vers le point de vente. Rattachez-le pour pouvoir le vendre en caisse.</div>
          </div>
          <button onClick={consolidate} disabled={consolidating} className="btn-primary shrink-0" data-testid="stock-consolidate-btn">
            {consolidating ? "Rattachement…" : "Rattacher le stock à la boutique"}
          </button>
        </div>
      )}

      <div className="inline-flex gap-1 bg-[#F1F5F9] p-1 rounded-xl w-fit" role="tablist">
        {[["stock", "État du stock"], ["movements", "Journal des mouvements"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setPage(1); }} data-testid={`stock-tab-${k}`} role="tab" aria-selected={tab === k}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === k ? "bg-white text-[#2563EB] shadow-sm" : "text-[#64748B] hover:text-[#334155]"}`}>{l}</button>
        ))}
      </div>

      {tab === "stock" && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input className="inp pl-9" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher un produit…" data-testid="stock-search-input" />
          </div>
          <button onClick={() => { setAlertOnly((v) => !v); setPage(1); }} data-testid="stock-filter-alert"
            className={`self-start inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${alertOnly ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C]" : "bg-white border-[#E7ECF3] text-[#64748B] hover:border-[#B91C1C]"}`}>
            Rupture et stock faible{alertOnly ? " ✕" : ""}
          </button>
        </div>
      )}

      {tab === "stock" ? (
        <div className="card">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full"><thead><tr>
              <th className="th">Produit</th><th className="th">Unité</th><th className="th text-right">{sLocName}</th>
              <th className="th text-right">En transit</th><th className="th text-right">Global</th>
              <th className="th text-right">Seuil</th><th className="th">Statut</th><th className="th text-right">Action</th>
            </tr></thead><tbody>
              {viewProducts.slice((page - 1) * 10, page * 10).map((p) => (
                <tr key={p.id} className="row" data-testid={`stock-row-${p.id}`}>
                  <td className="td font-medium text-[#0F172A]">{p.name}</td>
                  <td className="td">{p.base_unit}</td>
                  <td className="td text-right font-semibold">{locStock(p)}<PackEquiv p={p} qty={locStock(p)} /></td>
                  <td className="td text-right text-[#1D4ED8]">{p.in_transit || 0}<PackEquiv p={p} qty={p.in_transit} /></td>
                  <td className="td text-right">{p.stock}</td>
                  <td className="td text-right text-[#64748B]">{p.reorder_level}</td>
                  <td className="td">{stockStatus(p) ? <StatusBadge status={stockStatus(p)} /> : <span className="text-[#94A3B8] text-xs">—</span>}</td>
                  <td className="td text-right">{canWriteStock ? <button onClick={() => quickEntry(p)} className="btn-primary !py-1.5 !px-3" data-testid={`stock-entry-${p.id}`}><PackagePlus className="w-3.5 h-3.5" /> Entrée</button> : <span className="text-[#CBD5E1] text-xs">—</span>}</td>
                </tr>
              ))}
              {viewProducts.length === 0 && <tr><td colSpan={8} className="td text-center py-10 text-[#94A3B8]">Aucun produit</td></tr>}
            </tbody></table>
          </div>
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {viewProducts.slice((page - 1) * 10, page * 10).map((p) => (
              <div key={p.id} className="py-4 first:pt-1" data-testid={`stock-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-[#0F172A] text-base">{p.name}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="chip-slate">{p.base_unit}</span>
                      <span className="chip-slate">Seuil {p.reorder_level}</span>
                    </div>
                  </div>
                  {stockStatus(p) ? <StatusBadge status={stockStatus(p)} /> : <span className="text-[#94A3B8] text-xs shrink-0">—</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {sLoc !== "ALL" && (
                    <div className="rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] px-3 py-2 min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1D4ED8] truncate">{sLocName}</div>
                      <div className="text-xl font-extrabold text-[#0F172A]">{locStock(p)}</div>
                    </div>
                  )}
                  <div className={`rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 min-w-0 ${sLoc === "ALL" ? "col-span-2" : ""}`}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B] truncate">Stock global</div>
                    <div className="text-xl font-extrabold text-[#334155]">{p.stock}</div>
                  </div>
                </div>
                {(p.in_transit || 0) > 0 && <div className="mt-2"><span className="chip-amber">En transit : {p.in_transit} {p.base_unit}</span><PackEquiv p={p} qty={p.in_transit} className="mt-0.5" /></div>}
                {canWriteStock && (
                <div className="mt-3">
                  <button onClick={() => quickEntry(p)} className="btn-primary w-full justify-center !py-3 text-[15px]" data-testid={`stock-entry-m-${p.id}`}><PackagePlus className="w-5 h-5" /> Entrée de stock</button>
                </div>
                )}
              </div>
            ))}
            {viewProducts.length === 0 && <div className="text-center py-10 text-[#94A3B8]">Aucun produit</div>}
          </div>
          <Pagination page={page} setPage={setPage} total={viewProducts.length} />
        </div>
      ) : (
        <div className="card">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full"><thead><tr>
              <th className="th">Date</th><th className="th">Produit</th><th className="th">Type</th>
              <th className="th text-right">Quantité</th><th className="th">Emplacement</th><th className="th">Acteur</th><th className="th">Motif</th>
            </tr></thead><tbody>
              {visMoves.slice((page - 1) * 10, page * 10).map((m) => (
                <tr key={m.id} className="row">
                  <td className="td text-xs">{fmtDate(m.created_at)}</td>
                  <td className="td font-medium">{m.product_name}</td>
                  <td className="td">{MOVE_LABELS[m.type] || m.type}</td>
                  <td className={`td text-right font-semibold ${m.signed_qty >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{m.signed_qty > 0 ? "+" : ""}{m.signed_qty}</td>
                  <td className="td text-xs text-[#64748B]">{m.location || "—"}</td>
                  <td className="td text-xs">{m.actor_name}</td>
                  <td className="td text-xs text-[#64748B]">{m.reason || "—"}</td>
                </tr>
              ))}
              {visMoves.length === 0 && <tr><td colSpan={7} className="td text-center py-10 text-[#94A3B8]">Aucun mouvement</td></tr>}
            </tbody></table>
          </div>
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {visMoves.slice((page - 1) * 10, page * 10).map((m) => (
              <div key={m.id} className="py-3" data-testid={`movement-card-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[#0F172A] truncate">{m.product_name}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5">{MOVE_LABELS[m.type] || m.type} · {fmtDate(m.created_at)}</div>
                  </div>
                  <span className={`font-bold shrink-0 ${m.signed_qty >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{m.signed_qty > 0 ? "+" : ""}{m.signed_qty}</span>
                </div>
                <div className="text-xs text-[#64748B] mt-1">{m.location ? `${m.location} · ` : ""}{m.actor_name}{m.reason ? ` · ${m.reason}` : ""}</div>
              </div>
            ))}
            {visMoves.length === 0 && <div className="text-center py-10 text-[#94A3B8]">Aucun mouvement</div>}
          </div>
          <Pagination page={page} setPage={setPage} total={visMoves.length} />
        </div>
      )}

      <Modal open={moveOpen} onClose={() => setMoveOpen(false)} title="Entrée / sortie de stock" testId="movement-modal"
        footer={<><button className="btn-outline" onClick={() => setMoveOpen(false)}>Annuler</button><button className="btn-primary" onClick={submitMove} data-testid="submit-movement-btn">Enregistrer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Produit</label>
            <select className="inp" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value, unit: "base" })} data-testid="movement-product-select">
              <option value="">Sélectionner…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
          <div><label className="lbl">Type</label>
            <select className="inp" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="movement-type-select">
              <option value="reception">Réception (entrée)</option><option value="adjustment">Ajustement (+)</option>
              <option value="loss">Casse / Perte (−)</option><option value="return_in">Retour client (+)</option>
            </select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Quantité</label>
              <input type="number" className="inp" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="movement-qty-input" /></div>
            <div><label className="lbl">Unité</label>
              <select className="inp" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="movement-unit-select">
                {unitOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select></div>
          </div>
          {selProd && form.quantity && <p className="text-xs text-[#0E7490]">Cela {form.type === "loss" ? "retirera" : "ajoutera"} <b>{baseQtyPreview} {selProd.base_unit}</b> au stock (stock actuel : {selProd.stock} {selProd.base_unit}).<PackEquiv p={selProd} qty={baseQtyPreview} packaging={form.unit} /></p>}
          {form.type === "reception" && <div><label className="lbl">Coût d'achat (par {selProd?.base_unit || "unité"}, optionnel)</label>
            <input type="number" className="inp" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>}
          <div><label className="lbl">Motif</label>
            <input className="inp" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Ex : réapprovisionnement, casse…" /></div>
        </div>
      </Modal>

      <Modal open={invOpen} onClose={() => setInvOpen(false)} title="Session d'inventaire" wide testId="inventory-modal"
        footer={<><button className="btn-outline" onClick={() => setInvOpen(false)}>Annuler</button><button className="btn-success" onClick={submitInv} data-testid="submit-inventory-btn">Clôturer & ajuster</button></>}>
        <div className="space-y-2">
          <p className="text-sm text-[#64748B] mb-3">Inventaire à <b>{sLocName}</b>. Saisissez la quantité réellement comptée. Les écarts génèrent un ajustement audité.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E7ECF3]">
            <div><label className="lbl">Motif de l'ajustement <span className="text-[#B91C1C]">*</span></label>
              <select className="inp" value={invReason} onChange={(e) => setInvReason(e.target.value)} data-testid="inv-reason-select">
                <option value="">Choisir un motif…</option>
                <option value="casse">Casse</option>
                <option value="perte">Perte</option>
                <option value="vol">Vol</option>
                <option value="peremption">Péremption</option>
                <option value="erreur_comptage">Erreur de comptage</option>
                <option value="correction">Correction</option>
                <option value="autre">Autre</option>
              </select></div>
            {invReason === "autre" && <div><label className="lbl">Commentaire <span className="text-[#B91C1C]">*</span></label>
              <input className="inp" value={invComment} onChange={(e) => setInvComment(e.target.value)} placeholder="Précisez le motif" data-testid="inv-comment-input" /></div>}
          </div>
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-[#F8FAFC]">
              <div className="text-sm font-medium">{p.name}<span className="text-[#94A3B8]"> · théorique {locStock(p)}</span><PackEquiv p={p} qty={locStock(p)} /></div>
              <input type="number" defaultValue={locStock(p)} onChange={(e) => setCounts({ ...counts, [p.id]: e.target.value })}
                className="w-28 inp !py-1.5 text-right" data-testid={`inv-count-${p.id}`} />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
